import { memo, useMemo, useRef, useState } from 'react';
import type { TripListViewState } from '@/components/trips/table/types';
import type { BusinessTrip, TripUser } from '@/hooks/trips/types';
import {
  DATE_PLACEHOLDER,
  formatDateRange,
  formatSubmittedDate,
  getStatusBadge,
} from '@/components/trips/table/trip-list-utils';
import { useTranslations } from '@/i18n';
import type { Translator } from '@/i18n/useTranslations';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import ActionDropdown, { type DropdownAction } from '@/components/ui/ActionDropdown';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import TripExtendDialog from '@/components/trips/TripExtendDialog';
import TripShortenDialog from '@/components/trips/TripShortenDialog';
import { useRouter } from '@/lib/navigation';
import { useToast } from '@/components/ui/toast';
import { useApproveTrip } from '@/hooks/trips/useApproveTrip';
import { useDeclineTrip } from '@/hooks/trips/useDeclineTrip';
import { useCancelTrip } from '@/hooks/trips/useCancelTrip';
import { useDeleteTrip } from '@/hooks/trips/useDeleteTrip';
import { useExtendTrip, useShortenTrip } from '@/hooks/trips/useExtendTrip';
import { canShowApprovalActions } from '@/config/approval-actions';
import { format } from 'date-fns';

import { TripListPagination } from '@/components/trips/table/TripListPagination';

interface TripsListMobileProps {
  variant: 'default' | 'approvals';
  currentUserRoles?: readonly string[] | null;
  viewState: TripListViewState;
}

export default function TripsListMobile({
  variant,
  currentUserRoles = [],
  viewState,
}: TripsListMobileProps) {
  const t = useTranslations('trips');
  const tToasts = useTranslations('toasts');
  const { data, trips, isLoading, error } = viewState;

  if (isLoading && !data) {
    return (
      <div className="flex flex-col gap-4 bg-white">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={`trip-card-skeleton-${index}`}
            className="rounded-3xl border border-[#f0f0f0] bg-white p-4 shadow-[0px_18px_40px_-20px_rgba(31,31,31,0.16)]"
          >
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
            <div className="mt-4 flex flex-col gap-3">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-28" />
            </div>
            <div className="mt-4 flex gap-2">
              <Skeleton className="h-9 flex-1 rounded-[999px]" />
              <Skeleton className="h-9 flex-1 rounded-[999px]" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white">
        <div className="p-6 text-center">
          <div className="mb-2 text-red-500">{t('errorLoading')}</div>
          <div className="text-sm text-gray-600">{error.message}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 bg-white">
      {trips.length > 0 ? (
        trips.map((trip) => (
          <TripCardMobile
            key={trip.id}
            trip={trip}
            variant={variant}
            currentUserRoles={currentUserRoles}
            t={t}
            tToasts={tToasts}
          />
        ))
      ) : (
        <div className="rounded-3xl border border-dashed border-[#e2e2e2] bg-[#fafafa] px-4 py-10 text-center text-sm text-[#666]">
          {t('empty')}
        </div>
      )}

      <TripListPagination t={t} viewState={viewState} className="px-2" maxButtons={3} />
    </div>
  );
}

interface TripCardMobileProps {
  trip: BusinessTrip;
  variant: 'default' | 'approvals';
  currentUserRoles?: readonly string[] | null;
  t: Translator;
  tToasts: Translator;
}

const TripCardMobile = memo(function TripCardMobile({
  trip,
  variant,
  currentUserRoles,
  t,
  tToasts,
}: TripCardMobileProps) {
  const router = useRouter();
  const { showToast } = useToast();
  const approveTripMutation = useApproveTrip();
  const declineTripMutation = useDeclineTrip();
  const cancelTripMutation = useCancelTrip();
  const deleteTripMutation = useDeleteTrip();
  const extendTripMutation = useExtendTrip();
  const shortenTripMutation = useShortenTrip();
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExtendConfirm, setShowExtendConfirm] = useState(false);
  const [showShortenConfirm, setShowShortenConfirm] = useState(false);
  const [activeApproveTripId, setActiveApproveTripId] = useState<string | null>(null);
  const [activeDeclineTripId, setActiveDeclineTripId] = useState<string | null>(null);
  const [activeCancelTripId, setActiveCancelTripId] = useState<string | null>(null);
  const [activeDeleteTripId, setActiveDeleteTripId] = useState<string | null>(null);
  const [activeExtendTripId, setActiveExtendTripId] = useState<string | null>(null);
  const [activeShortenTripId, setActiveShortenTripId] = useState<string | null>(null);
  const [extendErrorMessage, setExtendErrorMessage] = useState<string | null>(null);
  const [shortenErrorMessage, setShortenErrorMessage] = useState<string | null>(null);
  const dropdownClosingRef = useRef(false);

  const isApprovingThisTrip = approveTripMutation.isPending && activeApproveTripId === trip.id;
  const isDecliningThisTrip = declineTripMutation.isPending && activeDeclineTripId === trip.id;
  const isCancellingThisTrip = cancelTripMutation.isPending && activeCancelTripId === trip.id;
  const isDeletingThisTrip = deleteTripMutation.isPending && activeDeleteTripId === trip.id;
  const isExtendingThisTrip = extendTripMutation.isPending && activeExtendTripId === trip.id;
  const isShorteningThisTrip = shortenTripMutation.isPending && activeShortenTripId === trip.id;
  const isActionPending =
    isApprovingThisTrip ||
    isDecliningThisTrip ||
    isCancellingThisTrip ||
    isDeletingThisTrip ||
    isExtendingThisTrip ||
    isShorteningThisTrip;

  const isCcEmployee = useMemo(() => {
    if (!currentUserRoles || currentUserRoles.length === 0) {
      return false;
    }

    return currentUserRoles
      .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
      .map((role) =>
        role
          .trim()
          .toUpperCase()
          .replace(/^ROLE_/, '')
      )
      .includes('CC_EMPLOYEE');
  }, [currentUserRoles]);

  const roamingApprovalLabel = useMemo(() => {
    if (!trip.roamingExpense) {
      return null;
    }
    return trip.roamingActivated
      ? t('statuses.approvedRoamingActive')
      : t('statuses.approvedRoamingPending');
  }, [t, trip.roamingActivated, trip.roamingExpense]);

  const statusLabelOverride =
    variant === 'default' && trip.status === 'approved' ? roamingApprovalLabel : null;

  const myStatusLabelOverride =
    variant === 'approvals' && trip.myState === 'approved' && isCcEmployee
      ? roamingApprovalLabel
      : null;

  const myStateBadge =
    trip.myState && trip.myState !== 'unknown'
      ? getStatusBadge(trip.myState, t, myStatusLabelOverride)
      : null;

  // Use availableActions from API to determine what actions the user can perform
  const availableActions = trip.availableActions ?? [];
  const canApprove = availableActions.includes('approve');
  const canDecline = availableActions.includes('decline');
  const canCancel = availableActions.includes('cancel');
  const canExtend = availableActions.includes('extend');
  const canShorten = availableActions.includes('shorten');
  // For draft trips, the API returns 'cancel' but we should show 'delete' instead
  const isDraft = trip.status === 'draft';
  const canDelete = isDraft && canCancel;

  // Show approval controls on approvals page if user can approve or decline
  // Hide for CC_EMPLOYEE, FUEL_ADMIN, and CEO_ASSISTANT roles
  const shouldShowApprovalControls =
    variant === 'approvals' &&
    canShowApprovalActions(currentUserRoles) &&
    (canApprove || canDecline || isActionPending);

  // Show action menu on default variant if there are any available actions
  // For draft trips, canDelete replaces canCancel
  const shouldShowActionMenu =
    variant === 'default' && (canDelete || (!isDraft && canCancel) || canExtend || canShorten);

  const dropdownActions: DropdownAction[] = useMemo(() => {
    if (!shouldShowActionMenu) {
      return [];
    }

    const actions: DropdownAction[] = [];

    // For draft trips, show Delete instead of Cancel
    if (canDelete) {
      actions.push({
        id: 'delete',
        label: t('actions.delete'),
        icon: 'delete',
        variant: 'destructive',
        onClick: () => {
          setShowDeleteConfirm(true);
        },
      });
    } else if (canCancel) {
      // For non-draft trips, show Cancel
      actions.push({
        id: 'cancel',
        label: t('actions.cancel'),
        icon: 'cancel',
        variant: 'destructive',
        onClick: () => {
          setShowCancelConfirm(true);
        },
      });
    }

    if (canExtend) {
      actions.push({
        id: 'extend',
        label: t('actions.extend'),
        icon: 'extend',
        onClick: () => {
          setShowExtendConfirm(true);
        },
      });
    }

    if (canShorten) {
      actions.push({
        id: 'shorten',
        label: t('actions.shrink'),
        icon: 'shrink',
        onClick: () => {
          setShowShortenConfirm(true);
        },
      });
    }

    return actions;
  }, [shouldShowActionMenu, canDelete, canCancel, canExtend, canShorten, t]);

  const handleApprove = () => setShowApproveConfirm(true);
  const handleReject = () => {
    if (!canDecline) {
      return;
    }
    setShowRejectConfirm(true);
  };

  const confirmApprove = () => {
    setActiveApproveTripId(trip.id);
    approveTripMutation.mutate(trip.id, {
      onSuccess: () => {
        showToast({ message: tToasts('tripApproved'), variant: 'success' });
      },
      onError: (err) => {
        console.error('Failed to approve trip:', err);
      },
      onSettled: () => {
        setShowApproveConfirm(false);
        setActiveApproveTripId(null);
      },
    });
  };

  const confirmReject = async () => {
    if (!canDecline) {
      setShowRejectConfirm(false);
      return;
    }
    setActiveDeclineTripId(trip.id);
    try {
      await declineTripMutation.mutate({ tripId: trip.id, comment: '' });
      console.log('Trip rejected successfully');
    } catch (err) {
      console.error('Failed to reject trip:', err);
    } finally {
      setShowRejectConfirm(false);
      setActiveDeclineTripId(null);
    }
  };

  const confirmCancel = async () => {
    if (!shouldShowActionMenu) {
      setShowCancelConfirm(false);
      return;
    }

    setActiveCancelTripId(trip.id);
    try {
      await cancelTripMutation.mutateAsync(trip.id);
      console.log('Trip cancelled successfully');
    } catch (err) {
      console.error('Failed to cancel trip:', err);
    } finally {
      setShowCancelConfirm(false);
      setActiveCancelTripId(null);
    }
  };

  const confirmDelete = async () => {
    if (!canDelete) {
      setShowDeleteConfirm(false);
      return;
    }

    setActiveDeleteTripId(trip.id);
    try {
      await deleteTripMutation.mutateAsync(trip.id);
      console.log('Trip deleted successfully');
    } catch (err) {
      console.error('Failed to delete trip:', err);
    } finally {
      setShowDeleteConfirm(false);
      setActiveDeleteTripId(null);
    }
  };

  const confirmExtend = async (newEndDate: Date) => {
    if (!shouldShowActionMenu) {
      setShowExtendConfirm(false);
      setExtendErrorMessage(null);
      return;
    }

    setActiveExtendTripId(trip.id);
    setExtendErrorMessage(null);
    try {
      const formattedDate = format(newEndDate, 'yyyy-MM-dd');
      const newTrip = await extendTripMutation.mutateAsync({
        tripId: trip.id,
        endDate: formattedDate,
      });
      showToast({
        message: (tToasts('tripExtended') as string) ?? t('actions.extend'),
        variant: 'success',
      });
      if (newTrip?.id) {
        router.push(`/trips/${newTrip.id}/edit?derived=extend`);
      }
      setShowExtendConfirm(false);
      setActiveExtendTripId(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('errorLoading') || 'Failed to extend trip';
      setExtendErrorMessage(message);
      setActiveExtendTripId(null);
    }
  };

  const handleExtendDialogClose = () => {
    setShowExtendConfirm(false);
    setExtendErrorMessage(null);
  };

  const confirmShorten = async (newEndDate: Date) => {
    if (!shouldShowActionMenu) {
      setShowShortenConfirm(false);
      setShortenErrorMessage(null);
      return;
    }

    setActiveShortenTripId(trip.id);
    setShortenErrorMessage(null);
    try {
      const formattedDate = format(newEndDate, 'yyyy-MM-dd');
      const newTrip = await shortenTripMutation.mutateAsync({
        tripId: trip.id,
        endDate: formattedDate,
      });
      showToast({
        message: (tToasts('tripShortened') as string) ?? t('actions.shrink'),
        variant: 'success',
      });
      if (newTrip?.id) {
        router.push(`/trips/${newTrip.id}/edit?derived=shorten`);
      }
      setShowShortenConfirm(false);
      setActiveShortenTripId(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('errorLoading') || 'Failed to shorten trip';
      setShortenErrorMessage(message);
      setActiveShortenTripId(null);
    }
  };

  const handleShortenDialogClose = () => {
    setShowShortenConfirm(false);
    setShortenErrorMessage(null);
  };

  const handleCardClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    const target = event.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('[role="menuitem"]') ||
      target.closest('[role="menu"]') ||
      target.closest('[data-radix-collection-item]')
    ) {
      return;
    }
    if (dropdownClosingRef.current) {
      return;
    }

    // Pass state to indicate where we came from
    const navigationState = variant === 'approvals' ? { from: '/approvals' } : undefined;
    router.push(`/trips/${trip.id}`, { state: navigationState });
  };

  const submittedDate = formatSubmittedDate(trip.submittedAt);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            handleCardClick(event);
          }
        }}
        className="rounded-3xl border border-[#f1f1f1] bg-white p-4 shadow-[0px_18px_40px_-20px_rgba(31,31,31,0.16)]"
      >
        <div className="flex items-start gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-[#9c9c9c] uppercase">{t('tableHeaders.number')}</span>
            <span className="text-base font-semibold text-[#292929]">{trip.number}</span>
          </div>
          <div className="ml-auto flex items-start justify-end gap-2">
            <div className="inline-flex max-w-[65%] justify-end">
              {getStatusBadge(trip.status, t, statusLabelOverride, {
                className:
                  'w-fit whitespace-normal break-words text-center leading-[1.2] px-3 py-1 text-[0.85rem]',
              })}
            </div>
            {shouldShowActionMenu && dropdownActions.length > 0 ? (
              <ActionDropdown
                actions={dropdownActions}
                onOpenChange={(open) => {
                  if (!open) {
                    dropdownClosingRef.current = true;
                    setTimeout(() => {
                      dropdownClosingRef.current = false;
                    }, 200);
                  }
                }}
                className="border-none bg-transparent p-0"
              />
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 text-sm text-[#444]">
          <MobileUserRow label={t('tableHeaders.initiator')} user={trip.initiator} />
          <MobileUserRow label={t('tableHeaders.author')} user={trip.author} />
          <MobileField
            label={t('tableHeaders.duration')}
            value={formatDateRange(trip.startDate, trip.endDate)}
          />
          <MobileField label={t('tableHeaders.submittedAt')} value={submittedDate} />
          {variant === 'approvals' && myStateBadge ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-[#666]">{t('tableHeaders.myStatus')}</span>
              {myStateBadge}
            </div>
          ) : null}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {shouldShowApprovalControls ? (
            <>
              {canDecline && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleReject();
                  }}
                  disabled={isActionPending}
                  className="flex-1 rounded-full text-sm font-medium"
                >
                  {isDecliningThisTrip ? (
                    <Spinner variant="circle" size={16} className="text-white" />
                  ) : (
                    t('actions.reject')
                  )}
                </Button>
              )}
              <Button
                variant="brand"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  handleApprove();
                }}
                disabled={isActionPending}
                className="flex-1 rounded-full text-sm font-medium"
              >
                {isApprovingThisTrip ? (
                  <Spinner variant="circle" size={16} className="text-white" />
                ) : (
                  t('actions.approve')
                )}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
        onConfirm={confirmApprove}
        title={t('approveConfirmation.title')}
        message={t('approveConfirmation.message')}
        confirmText={t('approveConfirmation.confirm')}
        cancelText={t('approveConfirmation.cancel')}
        confirmButtonVariant="brand"
        isLoading={isApprovingThisTrip}
      />

      <ConfirmationDialog
        isOpen={showRejectConfirm}
        onClose={() => setShowRejectConfirm(false)}
        onConfirm={confirmReject}
        title={t('rejectConfirmation.title')}
        message={t('rejectConfirmation.message')}
        confirmText={t('rejectConfirmation.confirm')}
        cancelText={t('rejectConfirmation.cancel')}
        confirmButtonVariant="danger"
        isLoading={isDecliningThisTrip}
      />

      <ConfirmationDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={confirmCancel}
        title={t('cancelConfirmation.title')}
        message={t('cancelConfirmation.message')}
        confirmText={t('cancelConfirmation.confirm')}
        cancelText={t('cancelConfirmation.cancel')}
        confirmButtonVariant="danger"
        isLoading={isCancellingThisTrip}
      />

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title={t('deleteConfirmation.title')}
        message={t('deleteConfirmation.message')}
        confirmText={t('deleteConfirmation.confirm')}
        cancelText={t('deleteConfirmation.cancel')}
        confirmButtonVariant="danger"
        isLoading={isDeletingThisTrip}
      />

      <TripExtendDialog
        isOpen={showExtendConfirm}
        onClose={handleExtendDialogClose}
        onConfirm={confirmExtend}
        currentEndDate={trip.endDate}
        isLoading={isExtendingThisTrip}
        errorMessage={extendErrorMessage ?? undefined}
      />

      <TripShortenDialog
        isOpen={showShortenConfirm}
        onClose={handleShortenDialogClose}
        onConfirm={confirmShorten}
        currentStartDate={trip.startDate}
        currentEndDate={trip.endDate}
        isLoading={isShorteningThisTrip}
        errorMessage={shortenErrorMessage ?? undefined}
      />
    </>
  );
});

function MobileField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-[#666]">{label}</span>
      <span className="font-medium text-[#292929]">{value ?? DATE_PLACEHOLDER}</span>
    </div>
  );
}

function MobileUserRow({ label, user }: { label: string; user: TripUser }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-[#666]">{label}</span>
      <div className="flex min-w-0 items-center gap-2">
        <span className="max-w-[11rem] truncate text-sm font-medium text-[#292929]">
          {user.name}
        </span>
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white">
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} className="h-full w-full object-cover" />
          ) : (
            <span className="text-xs font-medium">
              {user.name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .substring(0, 2)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
